import rison from 'rison';

import { IntegrationCategory } from '@gitbook/api-client';
import { assertNever, findInArray } from '@gitbook/util-ts';

import { getSpaceSideSheetKeys, type NavigationCommentsSideSheet } from './side-sheets';
import type {
    LocationDescriptor,
    NavigationScreenAccountSettings,
    NavigationScreenChangeRequestsFilters,
    NavigationScreenCollectionState,
    NavigationScreenContentGapsState,
    NavigationScreenLinkAuditState,
    NavigationScreenLegacyIntegrationsState,
    NavigationScreenOrgSettings,
    NavigationScreenSiteFindingsFilters,
    NavigationScreenSiteState,
    NavigationScreenSitesListState,
    NavigationScreenSpaceState,
    NavigationScreenState,
    NavigationScreenTriageState,
} from './types';
import { NavigationSiteSettingsSections } from './types';
import { formatRevision } from './utils';

/**
 * Resolve a screen to a location.
 */
export function resolveNavigationScreenToLocation(
    screen: NavigationScreenState
): LocationDescriptor {
    const params = new URLSearchParams();
    const pathname = resolveNavigationScreenToPath(screen, params);

    getSearchForNavigationScreen(screen, params);
    const strParams = params.toString();

    return {
        pathname,
        ...(screen.hash ? { hash: screen.hash } : {}),
        ...(strParams ? { search: `?${strParams}` } : {}),
    };
}

/**
 * Resolve a screen to a href.
 */
export function resolveNavigationScreenToHref(screen: NavigationScreenState): string {
    const location = resolveNavigationScreenToLocation(screen);
    const hash = location.hash
        ? location.hash.startsWith('#')
            ? location.hash
            : `#${location.hash}`
        : '';
    return `${location.pathname}${location.search || ''}${hash}`;
}

/**
 * Get the search property of the location for a screen
 */
function getSearchForNavigationScreen(
    screen: NavigationScreenState,
    params: URLSearchParams
): void {
    if (typeof screen.query === 'string') {
        params.append('q', screen.query);
    }

    if (screen.askAI) {
        params.append('ask_ai', 'true');
    }

    if (screen.coupon) {
        params.append('coupon', screen.coupon);
    }

    if (screen.ssoLogin) {
        if (screen.ssoLogin.email) {
            params.append('email', screen.ssoLogin.email);
        }
        if (screen.ssoLogin.noSSO) {
            params.append('no_sso', screen.ssoLogin.noSSO);
        }
    }
}

/**
 * Get path for a navigation state.
 */
function resolveNavigationScreenToPath(
    screen: NavigationScreenState,
    query: URLSearchParams
): string {
    let result = '/';

    switch (screen.type) {
        case 'error_notfound':
            break;
        case 'home':
            if (screen.organization) {
                result += `o/${screen.organization}/home`;
            } else {
                result += 'home';
            }
            break;
        case 'oauth_authorize':
            result += 'oauth/authorize';
            if (screen.sessionId) {
                query.set('sessionId', screen.sessionId);
            }
            if (screen.signup) {
                query.set('signup', 'true');
            }
            break;
        case 'join':
            result += 'join';
            break;
        case 'welcome':
            result += `welcome${screen.organizationType ? `/o/${screen.organizationType}` : ''}`;
            break;
        case 'account':
            result += getPathForAccountSettingsState(screen);
            break;
        case 'sites':
            result += getPathForSitesListState(screen, query);
            break;
        case 'site':
            result += getPathForSiteState(screen, query);
            break;
        case 'space':
            result += getPathForSpaceState(screen, query);
            break;
        case 'collection':
            result += getPathForCollectionState(screen);
            break;
        case 'site_preview_auth': {
            if (screen.redirect) {
                query.set('redirect', screen.redirect);
            }
            result += `o/${screen.organization}/sites/${screen.site}/preview/auth`;
            break;
        }
        case 'org_invite':
            result += `o/${screen.organization}/invite`;
            break;
        case 'site_invite':
            result += `o/${screen.organization}/sites/${screen.site}/members/invite`;
            break;
        case 'org_join':
            result += `invite/${screen.organization}/${screen.invite}`;
            break;
        case 'org_settings':
            result += getPathForOrgSettingsState(screen, query);
            break;
        case 'integrations_legacy':
            result += getPathForLegacyIntegrationsState(screen, query);
            break;
        case 'user':
            result += `u/${screen.user}`;
            break;
        case 'root':
            if (screen.organization) {
                result += `o/${screen.organization}`;
            }
            break;
        case 'trash':
            result += `o/${screen.organization}/trash`;
            break;
        case 'org_content':
            result += `o/${screen.organization}/content`;
            break;
        case 'styleguides':
            result += `o/${screen.organization}/styleguides`;
            break;
        case 'org_with_basename':
            result += `@${screen.organizationBaseName}/`;
            break;
        case 'change_requests': {
            result += `o/${screen.organization}/`;
            if (screen.site) {
                result += `sites/${screen.site}/`;
            }
            result += 'change-requests';

            if (screen.filters) {
                appendQueryParamsForChangeRequestsFilters(query, screen.filters);
            }
            break;
        }
        case 'triage':
            result += getPathForTriageState(screen, query);
            break;
        case 'content_gaps':
            result += getPathForContentGapsState(screen, query);
            break;
        case 'link_audit':
            result += getPathForLinkAuditState(screen, query);
            break;
        case 'styleguide_new':
            result += `o/${screen.organization}/sites/${screen.site}/styleguide/new`;
            break;
        case 'agent': {
            result += `o/${screen.organization}/sites/${screen.site}/agent`;
            if (screen.conversation) {
                result += `/${screen.conversation}`;
                // Only after a conversation, which the side sheet's path would otherwise stand in for.
                if (screen.sideSheet) {
                    result += `/~/${screen.sideSheet.type}`;
                    if (screen.sideSheet.tab) {
                        result += `/${screen.sideSheet.tab}`;
                    }
                }
            }
            break;
        }
        case 'agents': {
            result += `o/${screen.organization}/agents`;
            break;
        }
        default:
            assertNever(screen);
    }

    return result;
}

function getPathForOrgSettingsState(
    state: NavigationScreenOrgSettings,
    query: URLSearchParams
): string {
    let pathname = `o/${state.organization}/settings`;

    switch (state.tab.type) {
        case 'members':
            pathname += '/members';
            if (state.tab.memberId) {
                pathname += `/${state.tab.memberId}`;
                if (state.tab.detailsTab) {
                    pathname += `/${state.tab.detailsTab}`;
                }
            }
            break;

        case 'teams':
            pathname += '/teams';
            if (state.tab.teamId) {
                pathname += `/${state.tab.teamId}`;
                if (state.tab.detailsTab) {
                    pathname += `/${state.tab.detailsTab}`;
                }
            }
            break;

        case 'profile':
            break;

        case 'agents':
            pathname += '/agents';
            pathname += `/${state.tab.tab ?? 'settings'}`;
            break;
        case 'integrations':
            pathname += '/integrations';
            if (state.tab.integration) {
                pathname += `/${state.tab.integration}`;
                if (state.tab.tab === 'installations') {
                    pathname += '/installations';
                }
                if (state.tab.installation) {
                    pathname += `/~/installation/${state.tab.installation.step}`;
                    if (state.tab.installation.context) {
                        pathname += `/${state.tab.installation.context.type}/${state.tab.installation.context.id}`;
                    }
                }
            } else {
                if (
                    state.tab.category &&
                    Object.values(IntegrationCategory).includes(state.tab.category)
                ) {
                    query.set('category', state.tab.category);
                }
                if (state.tab.search) {
                    query.set('search', state.tab.search);
                }
                if (state.tab.provider) {
                    query.set('provider', state.tab.provider);
                }
            }
            break;
        case 'openapi':
            pathname += '/openapi';
            if (state.tab.spec) {
                pathname += `/${state.tab.spec}`;
            }
            break;
        case 'translations':
            pathname += '/translations';
            if (state.tab.view === 'glossary') {
                pathname += '/glossary';
            }
            break;
        case 'audit-logs':
            pathname += '/audit-logs';
            if (state.tab.eventId) {
                pathname += `/${state.tab.eventId}`;
            }
            break;

        case 'plans':
            pathname += '/plans';
            if (state.tab.interval) {
                query.append('interval', state.tab.interval);
            }
            break;
        default:
            pathname += `/${state.tab.type}`;
            break;
    }

    return pathname;
}

function getPathForLegacyIntegrationsState(
    state: NavigationScreenLegacyIntegrationsState,
    query: URLSearchParams
): string {
    if (state.organization) {
        return getPathForOrgSettingsState(
            {
                type: 'org_settings',
                organization: state.organization,
                tab: state.integration
                    ? {
                          type: 'integrations',
                          integration: state.integration,
                          tab: state.tab,
                          installation: state.installation,
                      }
                    : {
                          type: 'integrations',
                          category: state.category,
                          search: state.search,
                          provider: state.provider,
                      },
            },
            query
        );
    }

    let pathname = 'integrations';
    if (state.integration) {
        pathname += `/${state.integration}`;
        if (state.tab === 'installations') {
            pathname += '/installations';
        }
        if (state.installation) {
            pathname += `/~/installation/${state.installation.step}`;
            if (state.installation.context) {
                pathname += `/${state.installation.context.type}/${state.installation.context.id}`;
            }
        }
    } else {
        if (state.category) {
            query.set('category', state.category);
        }
        if (state.search) {
            query.set('search', state.search);
        }
        if (state.provider) {
            query.set('provider', state.provider);
        }
    }

    return pathname;
}

function getPathForAccountSettingsState(state: NavigationScreenAccountSettings): string {
    if (state.tab === 'profile') {
        return 'account';
    }

    return `account/${state.tab}${
        state.tab === 'developer' && state.developerTab ? `/${state.developerTab}` : ''
    }`;
}

function getPathForSiteState(state: NavigationScreenSiteState, query: URLSearchParams): string {
    let pathname = '';

    if (state.billing) {
        query.set('billing', state.billing);
    }

    if (state.organization) {
        pathname += `o/${state.organization}/`;
    }

    pathname += `sites/${state.site}`;

    if (state.tab?.type) {
        switch (state.tab.type) {
            case 'analytics':
                pathname += '/analytics';
                pathname += `/${state.tab.report ?? 'traffic'}`;

                if (state.tab.question) {
                    pathname += `/questions/${state.tab.question}`;
                    if (state.tab.answer) {
                        pathname += `/answers/${state.tab.answer}`;
                    }
                } else if (state.tab.topic) {
                    pathname += `/topics/${state.tab.topic}`;
                }

                if (state.tab.filters) {
                    query.set('filters', rison.encode_object(state.tab.filters));
                }
                break;
            case 'settings':
                pathname += '/settings';
                pathname += `/${
                    findInArray(state.tab.section, NavigationSiteSettingsSections) ?? 'general'
                }`;
                break;
            case 'customization':
                pathname += '/customization';
                if (state.tab.siteSpaceId) {
                    pathname += `/v/${state.tab.siteSpaceId}`;
                }

                pathname += `/${state.tab.tab}`;
                break;
            case 'preview':
                pathname += '/preview';
                break;
            case 'structure':
                pathname += '/structure';
                if (state.tab.selected) {
                    query.set('selected', state.tab.selected);
                }
                break;
            case 'members':
                pathname += '/members';
                break;
            default:
                assertNever(state.tab);
        }
    }

    if (state.sideSheet) {
        if (state.sideSheet.type === 'integrations') {
            pathname = getPathForIntegrationsModalState(pathname, state, query);
        } else {
            pathname = appendPath(pathname, `~/${state.sideSheet.type}`);

            if ('siteSpaceId' in state.sideSheet && state.sideSheet.siteSpaceId) {
                pathname += `/v/${state.sideSheet.siteSpaceId}`;
            }

            if ('tab' in state.sideSheet) {
                pathname += `/${state.sideSheet.tab}`;
            }
        }
    }

    return pathname;
}

function getPathForTriageState(state: NavigationScreenTriageState, query: URLSearchParams): string {
    let pathname = `o/${state.organization}/sites/${state.site}/triage`;

    if (state.view === 'settings') {
        pathname += '/settings';
    } else if (state.view === 'scans') {
        pathname += '/scans';
        if (state.scan) {
            pathname += `/${state.scan}`;
        }
        if (state.scanFilters?.topic) {
            query.set('topic', state.scanFilters.topic);
        }
        if (state.scanFilters?.status) {
            query.set('status', state.scanFilters.status);
        }
    } else {
        if (state.finding) {
            pathname += `/${state.finding}`;
        }
        appendSiteFindingsFilterParams(query, state.filters);
    }

    return pathname;
}

function getPathForContentGapsState(
    state: NavigationScreenContentGapsState,
    query: URLSearchParams
): string {
    let pathname = `o/${state.organization}/sites/${state.site}/content-gaps`;

    if (state.view === 'settings') {
        return `${pathname}/settings`;
    }

    if (state.finding) {
        // The finding detail is a self-contained page; the list's filters and ordering are
        // list-only state, so they are dropped from the URL once a finding is selected.
        pathname += `/${state.finding}`;
        return pathname;
    }

    appendSiteFindingsFilterParams(query, state.filters);
    if (state.orderBy) {
        query.set('orderBy', state.orderBy);
    }

    return pathname;
}

function getPathForLinkAuditState(
    state: NavigationScreenLinkAuditState,
    query: URLSearchParams
): string {
    let pathname = `o/${state.organization}/sites/${state.site}/link-audit`;

    if (state.view === 'settings') {
        return `${pathname}/settings`;
    }

    if (state.finding) {
        // Keep the list state so going back to the list restores it.
        pathname += `/${state.finding}`;
    } else if (state.view === 'analysis') {
        pathname += '/analysis';
        if (state.analyticsFilters) {
            query.set('filters', rison.encode_object(state.analyticsFilters));
        }
        return pathname;
    }

    appendSiteFindingsFilterParams(query, state.filters);
    if (state.orderBy) {
        query.set('orderBy', state.orderBy);
    }
    if (state.url) {
        query.set('url', state.url);
    }

    return pathname;
}

/** Write side of `extractSiteTriageFilterParams`, shared by the triage and content gaps screens. */
function appendSiteFindingsFilterParams(
    query: URLSearchParams,
    filters: NavigationScreenSiteFindingsFilters | undefined
): void {
    if (filters?.topic) {
        query.set('topic', filters.topic);
    }
    if (filters?.severity) {
        query.set('severity', filters.severity);
    }
    if (filters?.status) {
        query.set('status', filters.status);
    }
    if (filters?.type) {
        query.set('type', filters.type);
    }
    if (filters?.source) {
        for (const source of filters.source) {
            query.append('source', source);
        }
    }
    if (filters?.hasChangeRequests !== undefined) {
        query.set('hasChangeRequests', String(filters.hasChangeRequests));
    }
    if (filters?.dateRange) {
        query.set('dateRange', filters.dateRange);
    }
    if (filters?.state) {
        query.set('state', filters.state);
    }
}

function getPathForSitesListState(
    state: NavigationScreenSitesListState,
    query: URLSearchParams
): string {
    let pathname = '';

    if (state.organization) {
        pathname += `o/${state.organization}/`;
    }

    pathname += 'sites';

    if (state.status === 'published' || state.status === 'unpublished') {
        query.set('status', state.status);
    } else {
        query.delete('status');
    }

    if (state.search) {
        query.set('search', state.search);
    } else {
        query.delete('search');
    }

    return pathname;
}

function getPathForSpaceState(state: NavigationScreenSpaceState, query: URLSearchParams): string {
    let pathname = '';

    if (state.organization) {
        pathname += `o/${state.organization}/`;
    }

    if (state.site) {
        pathname += `sites/${state.site}/`;
    }

    pathname += `s/${state.space}/`;

    const defaultMode = state.changeRequest ? 'overview' : 'edit';
    if (state.mode && state.mode !== defaultMode) {
        pathname += `~/${state.mode}/`;
    }

    if (state.changeRequest) {
        pathname += `~/changes/${state.changeRequest}/`;

        // The selected change only means something on the overview.
        if (state.change && (!state.mode || state.mode === 'overview')) {
            query.set('change', state.change);
        }
    }

    if (state.revision) {
        pathname += state.baseRevision
            ? `~/revisions/${state.baseRevision}...${formatRevision(state.revision)}/`
            : `~/revisions/${formatRevision(state.revision)}/`;
    }

    if (state.reusableContent) {
        pathname += `~/reusable/${state.reusableContent}/`;
    }

    if (state.page !== undefined) {
        pathname += `${state.page && state.page[0] === '/' ? state.page.slice(1) : state.page}`;
    }

    if (state.sideSheet) {
        const keys = getSpaceSideSheetKeys(state);

        // @ts-ignore
        if (!keys.includes(state.sideSheet.type)) {
            if (process.env.NODE_ENV === 'development') {
                throw new Error(
                    `Development error: "${state.sideSheet.type}" is not a valid side-sheet for the current space screen ${JSON.stringify(state)}. It can indicate that a button is rendered that could generate an invalid link.`
                );
            }
            return pathname;
        }

        if (state.sideSheet.type === 'comments') {
            pathname = getPathForCommentsSideSheet(pathname, state.sideSheet, query);
        } else if (state.sideSheet.type === 'integrations') {
            pathname = getPathForIntegrationsModalState(pathname, state, query);
        } else if (state.sideSheet.type === 'change-requests') {
            pathname = appendPath(pathname, '~/change-requests');
            appendQueryParamsForChangeRequestsFilters(query, state.sideSheet.filters ?? {});
        } else {
            pathname = appendPath(pathname, `~/${state.sideSheet.type}`);

            if ('tab' in state.sideSheet) {
                pathname += `/${state.sideSheet.tab}`;
            }
        }
    }

    return pathname;
}

function getPathForCollectionState(state: NavigationScreenCollectionState): string {
    let pathname = '';

    if (state.organization) {
        pathname += `o/${state.organization}/`;
    }

    pathname += `c/${state.collection}`;

    if (state.sideSheet) {
        pathname = appendPath(pathname, `~/${state.sideSheet.type}`);

        if ('tab' in state.sideSheet) {
            pathname += `/${state.sideSheet.tab}`;
        }
    }

    return pathname;
}

function getPathForIntegrationsModalState(
    path: string,
    state: NavigationScreenSpaceState | NavigationScreenSiteState,
    query: URLSearchParams
): string {
    if (!state.sideSheet) {
        return '';
    }

    let pathname = appendPath(path, `~/${state.sideSheet.type}`);

    if (state.sideSheet.type === 'integrations') {
        if (state.sideSheet.view) {
            if (state.sideSheet.view.type === 'integration') {
                pathname += `/${state.sideSheet.view.integration}`;

                switch (state.sideSheet.view.state) {
                    case 'configure':
                        pathname += '/configure';
                        break;
                    default:
                        pathname = String(pathname);
                        break;
                }

                return pathname;
            }

            if (state.sideSheet.view.type === 'installations') {
                pathname += '/installations';
            } else if (state.sideSheet.view.type === 'category') {
                pathname += '/category';

                const { category, search, provider } = state.sideSheet.view;

                if (category && Object.values(IntegrationCategory).includes(category)) {
                    pathname += `/${category}`;
                } else {
                    pathname += '/all';
                }

                if (search) {
                    query.set('search', search);
                } else {
                    query.delete('search');
                }

                if (provider) {
                    query.set('provider', provider);
                } else {
                    query.delete('provider');
                }
            }
        } else {
            pathname += '/installations';
        }
    }

    return pathname;
}

function getPathForCommentsSideSheet(
    path: string,
    sideSheet: NavigationCommentsSideSheet,
    query: URLSearchParams
): string {
    let pathname = appendPath(path, '~/comments');

    if (sideSheet.comment) {
        pathname = appendPath(pathname, sideSheet.comment);
    }

    if (sideSheet.context && ['preview'].includes(sideSheet.context)) {
        query.append('context', sideSheet.context);
    }

    if (sideSheet.node) {
        query.append('node', sideSheet.node);
    }

    if (sideSheet.tableCell) {
        query.append('record', sideSheet.tableCell.record);
        query.append('definition', sideSheet.tableCell.definition);
    }

    return pathname;
}

function appendQueryParamsForChangeRequestsFilters(
    query: URLSearchParams,
    filters: NavigationScreenChangeRequestsFilters
): void {
    if (filters.status) {
        for (const status of filters.status) {
            query.append('status', status);
        }
    }
    if (filters.contributor) {
        query.set('contributor', filters.contributor);
    }
    if (filters.creator) {
        query.set('creator', filters.creator);
    }
    if (filters.requestedReview) {
        query.set('requestedReview', filters.requestedReview);
    }
    if (filters.space) {
        query.set('space', filters.space);
    }
    if (filters.finding) {
        query.set('finding', filters.finding);
    }
}

/** Append two paths */
function appendPath(base: string, more: string): string {
    return `${base}${base[base.length - 1] === '/' ? '' : '/'}${more}`;
}
